/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the BLOCK selection layer
   (Phase 1 item 4, docs/MASTERS_UNIFIED_PLAN.md).

   Two selections live on this sheet and they must never fight:

     · the TEXT selection is ProseMirror's, untouched — caret, typing,
       shift-arrow, double-click-a-word all behave exactly as a word
       processor does, because none of it is re-implemented here;
     · the BLOCK selection is DISCRETE — a set of whole blocks named by
       id, made from the gutter handles, obeying the app-wide Explorer
       rules (`renderer/lib/listSelection.ts`).

   The block selection lives in a ProseMirror plugin rather than in
   React state for two reasons: it has to survive (and be remapped by)
   every transaction, and `designer/commands.ts` has to be able to READ
   it — `selectedBlocks` prefers it over the text selection, which is
   what makes every existing block command (level, duplicate, delete,
   no-number, skip-at-export, move) act on the whole gutter selection
   with no second implementation.

   Everything here is a plain function over EditorState so the rules are
   provable without a DOM (`blockSelect.test.ts`).
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  BLOCK_NODE_NAMES,
  expandToGroups,
  explicitBlocksKey,
  groupIdOf,
} from '../designer/commands';
import { nextSelectionByKey, type SelectionMods } from '../lib/listSelection';
import { snapBoundaryOutOfGroup } from '../../shared/block-groups';

export { explicitBlocksKey as blockSelectKey };

export interface BlockRef {
  id: string;
  node: PMNode;
  pos: number;
}

export interface BlockSelectState {
  /** Block ids, in DOCUMENT order. */
  ids: string[];
  /** Where the next Shift range starts. */
  anchor: string | null;
  /** Cached paint; rebuilt only when the ids or the document move. */
  deco: DecorationSet;
}

/** Every top-level block, in document order. */
export function topLevelBlocks(state: EditorState): BlockRef[] {
  const out: BlockRef[] = [];
  state.doc.forEach((node, pos) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    out.push({ id: (node.attrs.blockId as string) || '', node, pos });
  });
  return out;
}

/** The stable list a Shift range runs along: block ids, document order. */
export function blockIdOrder(state: EditorState): string[] {
  return topLevelBlocks(state)
    .map((b) => b.id)
    .filter(Boolean);
}

/**
 * The blocks the TEXT selection covers — a highlighted passage that spans
 * three paragraphs answers with those three ids. A collapsed caret answers
 * with the one block it sits in, which is why the caller checks for more than
 * one before treating it as "the user has already picked a run".
 */
export function blockIdsInTextSelection(state: EditorState): string[] {
  const { from, to } = state.selection;
  const out: string[] = [];
  for (const b of topLevelBlocks(state)) {
    const end = b.pos + b.node.nodeSize;
    if (end <= from || b.pos >= to) {
      if (!(from === to && from >= b.pos && from <= end)) continue;
    }
    if (b.id) out.push(b.id);
  }
  return out;
}

/** The plugin's own state. `explicitBlocksKey` is declared next to
 *  `selectedBlocks` and deliberately knows only about `ids` — the anchor and
 *  the cached paint are this layer's business, so they are read back here. */
const readState = (state: EditorState): BlockSelectState | undefined =>
  explicitBlocksKey.getState(state) as BlockSelectState | undefined;

/** Read the current block selection. Never undefined — an editor without the
 *  plugin (the Designer) simply has nothing selected.
 *
 *  Ids whose blocks have left the document are pruned HERE, at read time,
 *  rather than in the plugin's own state (Tranche 4 item 1): deleting the
 *  selected blocks must LOOK like it cleared the selection — nothing painted,
 *  no count, whole-block commands back on the caret — while one Ctrl+Z brings
 *  the blocks back AND the selection with them. That only works if the ids
 *  survive the delete, because the selection itself is deliberately outside
 *  the undo stack (`setBlockSelection` sets `addToHistory: false`), so undo
 *  has nothing of its own to restore. */
export function blockSelection(state: EditorState): { ids: string[]; anchor: string | null } {
  const value = readState(state);
  if (!value || !value.ids.length) return { ids: [], anchor: null };
  const ids = prune(state, value.ids);
  // The anchor is pruned against the DOCUMENT, not against the selection: in
  // Explorer, Ctrl-clicking a row OFF still leaves the anchor on it, and the
  // next Shift-click ranges from there.
  const anchor = value.anchor && blockIdOrder(state).includes(value.anchor) ? value.anchor : null;
  return { ids, anchor };
}

/** Replace the block selection. `ids` are re-ordered to document order so
 *  every consumer — paint, commands, drag — reads one truth. */
export function setBlockSelection(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  ids: string[],
  anchor: string | null
): boolean {
  const order = blockIdOrder(state);
  const want = new Set(ids);
  const ordered = order.filter((id) => want.has(id));
  const current = blockSelection(state);
  if (
    current.anchor === anchor &&
    current.ids.length === ordered.length &&
    current.ids.every((id, i) => id === ordered[i])
  ) {
    return false;
  }
  if (dispatch) {
    // Selection is not an edit: it must never land in the undo stack, or one
    // Ctrl+Z would undo a click instead of the typing the user means.
    dispatch(
      state.tr.setMeta(explicitBlocksKey, { ids: ordered, anchor }).setMeta('addToHistory', false)
    );
  }
  return true;
}

/** Drop the block selection entirely (Escape, a plain click on the sheet). */
export function clearBlockSelection(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  if (!blockSelection(state).ids.length) return false;
  return setBlockSelection(state, dispatch, [], null);
}

/**
 * Explorer semantics for a click on block `id`'s gutter handle — the same
 * `nextSelectionByKey` the forma canvas and the dirty reader stand on, with
 * DOCUMENT order as the list a Shift range runs along.
 */
export function clickBlockHandle(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  id: string,
  mods: SelectionMods
): boolean {
  const current = blockSelection(state);
  const next = nextSelectionByKey(current.ids, current.anchor, id, blockIdOrder(state), mods);
  // A GROUP answers as one row: clicking any block of it takes the whole run,
  // and Ctrl/Shift toggle it as a unit — the Explorer rules are untouched,
  // they simply run over a list where a group is one item.
  //
  // Ctrl-click is the exception that proves it. Explorer's toggle drops ONE
  // id, and re-expanding that answer would put the id straight back — a group
  // could never be Ctrl-deselected. So a Ctrl-click on a group that is
  // already selected whole subtracts the whole run, which is the same
  // "one row, one toggle" rule read from the other side.
  const members = expandToGroups(state.doc, [id]);
  if (mods.ctrl && !mods.shift && members.size > 1) {
    const picked = new Set(current.ids);
    if ([...members].every((m) => picked.has(m))) {
      return setBlockSelection(
        state,
        dispatch,
        current.ids.filter((x) => !members.has(x)),
        next.anchor
      );
    }
  }
  const ids = [...expandToGroups(state.doc, next.selection)];
  return setBlockSelection(state, dispatch, ids, next.anchor);
}

/**
 * A drop can never land INSIDE a group — it lands before or after it, whichever
 * edge the pointer was nearer. One rule, applied here rather than at each of
 * the three call sites (gutter drag, Alt+Shift+Arrow, a nudge), so a group
 * stays contiguous no matter which of them asked. The rule itself lives in
 * `shared/block-groups.ts`, the same one the drop LINE and the module insert
 * read, so the line can never promise a landing this refuses.
 *
 * Blocks that are themselves travelling do not count: dropping a group back
 * into the gap it came from is not "splitting" anything.
 */
function snapOutOfGroup(all: BlockRef[], target: number, moving: Set<string>): number {
  return snapBoundaryOutOfGroup(
    all.map((b) => ({ id: b.id, group: groupIdOf(b.node) })),
    target,
    { moving }
  );
}

/**
 * Move the named blocks so they land at BOUNDARY `targetIndex` — the gap
 * before block `targetIndex` in the CURRENT list, `list.length` meaning the
 * end of the document. One transaction, so one Ctrl+Z puts the document back.
 *
 * The blocks are cut LAST first (earlier positions stay valid) and the drop
 * point is carried through the transaction's own mapping, which is what makes
 * a NON-CONTIGUOUS selection land as one contiguous run — the whole point of
 * being able to Ctrl-click four scattered paragraphs and file them together.
 */
export function moveBlocksTo(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  ids: string[],
  targetIndex: number
): boolean {
  const all = topLevelBlocks(state);
  if (!all.length) return false;
  // Naming one block of a group moves the whole group (`expandToGroups`), and
  // the landing is pushed clear of any other group it would have split.
  const want = expandToGroups(state.doc, ids);
  const moving = all.filter((b) => want.has(b.id));
  if (!moving.length) return false;

  const target = snapOutOfGroup(all, Math.max(0, Math.min(targetIndex, all.length)), want);
  // Nothing to do: the run is already contiguous and already sits at this gap.
  const firstIdx = all.indexOf(moving[0]);
  const contiguous = moving.every((b, i) => all[firstIdx + i] === b);
  if (contiguous && (target === firstIdx || target === firstIdx + moving.length)) return false;

  const anchorPos = target < all.length ? all[target].pos : state.doc.content.size;
  const nodes = moving.map((b) => b.node);

  const tr = state.tr;
  for (let i = moving.length - 1; i >= 0; i--) {
    tr.delete(moving[i].pos, moving[i].pos + moving[i].node.nodeSize);
  }
  // bias -1: a gap whose preceding block was just cut collapses onto the cut,
  // which is exactly where the run should re-appear.
  const landing = tr.mapping.map(anchorPos, -1);
  tr.insert(landing, nodes);
  // Take the caret with the text, the way Alt+Shift+Arrow already does.
  const caret = Math.min(landing + 1, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/** Where a run of blocks would land if it moved `delta` places — the
 *  boundary index Alt+Shift+Arrow and a one-slot nudge both need. */
export function nudgeTarget(state: EditorState, ids: string[], delta: -1 | 1): number | null {
  const all = topLevelBlocks(state);
  const want = new Set(ids);
  const idx = all.map((b, i) => (want.has(b.id) ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return null;
  if (delta < 0) {
    const first = idx[0];
    return first === 0 ? null : first - 1;
  }
  const last = idx[idx.length - 1];
  return last === all.length - 1 ? null : last + 2;
}

/* ─── The paint ─── */

function build(state: EditorState, ids: string[]): DecorationSet {
  if (!ids.length) return DecorationSet.empty;
  const want = new Set(ids);
  const decorations: Decoration[] = [];
  for (const b of topLevelBlocks(state)) {
    if (!b.id || !want.has(b.id)) continue;
    decorations.push(
      Decoration.node(b.pos, b.pos + b.node.nodeSize, {
        class: 'sps-blk-picked',
        'data-block-picked': 'true',
      })
    );
  }
  return DecorationSet.create(state.doc, decorations);
}

/** Ids whose blocks are still in the document — a selected block that was
 *  deleted must not keep voting on what "the blocks I mean" is. Applied by
 *  `blockSelection` and by the paint, never to the stored state (see the note
 *  on `blockSelection`: the stored ids are what makes undo restore the
 *  selection along with the blocks). */
function prune(state: EditorState, ids: string[]): string[] {
  if (!ids.length) return ids;
  const alive = new Set(blockIdOrder(state));
  const kept = ids.filter((id) => alive.has(id));
  return kept.length === ids.length ? ids : kept;
}

/** The plugin on its own, so a test can build a bare EditorState with it —
 *  the rules are then provable without an editor or a DOM. */
export function blockSelectPlugin(): Plugin<BlockSelectState> {
  return new Plugin<BlockSelectState>({
    key: explicitBlocksKey,
    state: {
      init: () => ({ ids: [], anchor: null, deco: DecorationSet.empty }),
      apply: (tr, value, _old, newState) => {
        const meta = tr.getMeta(explicitBlocksKey) as
          | { ids: string[]; anchor: string | null }
          | undefined;
        if (meta) {
          return { ids: meta.ids, anchor: meta.anchor, deco: build(newState, meta.ids) };
        }
        if (!tr.docChanged) return value;
        // The ids are kept VERBATIM — never pruned here. A delete must be
        // undoable back into its selection (Tranche 4 item 1), and the
        // selection is outside the undo stack, so the only thing that can
        // remember it across the delete is this state. Everything that READS
        // the selection prunes to the living blocks (`blockSelection`), and
        // `build` only ever paints blocks that exist, so a stale id is
        // invisible everywhere until undo makes it real again.
        //
        // The document moved, so the node decorations must be re-derived from
        // the new positions — ids are stable, positions are not.
        return { ...value, deco: build(newState, prune(newState, value.ids)) };
      },
    },
    props: {
      decorations: (state) => readState(state)?.deco ?? DecorationSet.empty,
    },
  });
}

export const BlockSelect = Extension.create({
  name: 'specStudioBlockSelect',
  addProseMirrorPlugins() {
    return [blockSelectPlugin()];
  },
});
